import { DeliveryZone } from '@flipdish/api-client-typescript';
import get from 'lodash/get';

import * as actions from '../actions/deliveryzones.actions';
import { appsConstants } from '../constants/apps.constants';
import { deliveryZonesConstants } from '../constants/deliveryzones.constants';
import {
  type DeliveryZoneDraftPatchPayload,
  type DeliveryZonesDraftState,
  type DeliveryZonesDraftStatus,
  initialDraftState,
} from '../models/deliveryZonesDraft.model';

const {
  DELIVERY_ZONE_LOAD_ALL_SUCCESS,
  DELIVERY_ZONE_LOAD_ALL_FAILURE,
  DELIVERY_ZONE_STATE_RESET,
  DELIVERY_ZONE_CREATE_REQUEST,
  DELIVERY_ZONE_CREATE_FAILURE,
  DELIVERY_ZONE_UPDATE_FAILURE,
  DELIVERY_ZONE_REMOVE_SUCCESS,
  DELIVERY_ZONE_REMOVE_FAILURE,
  DELIVERY_ZONE_SELECTION_CHANGE,
  DELIVERY_ZONE_ERROR_RESET,
  DELIVERY_ZONE_LOCAL_EDIT_FLAG,
  DELIVERY_ZONE_DRAFT_PATCH,
  DELIVERY_ZONE_DRAFT_DISCARD,
  DELIVERY_ZONE_DRAFT_CLEAR,
  DELIVERY_ZONE_DRAFT_SET_STATUS,
  DELIVERY_ZONE_DRAFT_SET_PROGRESS,
  DELIVERY_ZONE_DRAFT_SET_ERROR,
  DELIVERY_ZONE_DRAFT_SET_CONFLICT,
} = deliveryZonesConstants;

export type {
  DeliveryZoneDraftPatchPayload,
  DeliveryZoneDraftUpdateExisting,
  DeliveryZonesDraftState,
  DeliveryZonesDraftStatus,
} from '../models/deliveryZonesDraft.model';
export { initialDraftState } from '../models/deliveryZonesDraft.model';

export type DeliveryZoneState = Readonly<{
  selectedId: number;
  initLoad: boolean;
  error: string | Error | null;
  isLocalEdit?: boolean;
  draft: DeliveryZonesDraftState;
}>;

const computeDirty = (draft: DeliveryZonesDraftState): boolean =>
  draft.pendingCreates.length > 0 ||
  Object.keys(draft.pendingUpdates).length > 0 ||
  draft.pendingDeletes.length > 0;

const applyPatch = (
  draft: DeliveryZonesDraftState,
  payload: DeliveryZoneDraftPatchPayload
): DeliveryZonesDraftState => {
  let pendingCreates = [...draft.pendingCreates];
  let pendingUpdates = { ...draft.pendingUpdates };
  let pendingDeletes = [...draft.pendingDeletes];

  if (payload.upsertPendingCreate) {
    const { clientId, zone } = payload.upsertPendingCreate;
    const idx = pendingCreates.findIndex((c) => c.clientId === clientId);
    if (idx >= 0) {
      pendingCreates = [...pendingCreates];
      pendingCreates[idx] = { clientId, zone };
    } else {
      pendingCreates = [...pendingCreates, { clientId, zone }];
    }
  }

  if (payload.removePendingCreate) {
    pendingCreates = pendingCreates.filter((c) => c.clientId !== payload.removePendingCreate);
  }

  if (payload.updateExisting) {
    const id = payload.updateExisting.Id;
    const prev = pendingUpdates[id];
    pendingUpdates = {
      ...pendingUpdates,
      [id]: prev ? { ...prev, ...payload.updateExisting } : { ...payload.updateExisting },
    };
  }

  if (payload.addPendingDelete != null) {
    const id = payload.addPendingDelete;
    if (!pendingDeletes.includes(id)) {
      pendingDeletes = [...pendingDeletes, id];
    }
    pendingUpdates = Object.fromEntries(
      Object.entries(pendingUpdates).filter(([key]) => Number(key) !== id)
    ) as Record<number, DeliveryZone>;
  }

  if (payload.removePendingDelete != null) {
    pendingDeletes = pendingDeletes.filter((zoneId) => zoneId !== payload.removePendingDelete);
  }

  const next: DeliveryZonesDraftState = {
    ...draft,
    storeId: payload.storeId,
    pendingCreates,
    pendingUpdates,
    pendingDeletes,
    lastError: null,
  };

  return {
    ...next,
    dirty: computeDirty(next),
  };
};

const initialState: DeliveryZoneState = {
  selectedId: 0,
  initLoad: false,
  error: null,
  draft: initialDraftState,
};

export function deliveryZones(state = initialState, action) {
  switch (action.type) {
    case appsConstants.SELECTED_APP_CHANGED:
      return {
        ...state,
        initLoad: false,
        error: null,
        draft: initialDraftState,
      };
    case DELIVERY_ZONE_LOCAL_EDIT_FLAG:
      return {
        ...state,
        isLocalEdit: action.payload,
      };
    case DELIVERY_ZONE_LOAD_ALL_SUCCESS:
      return {
        ...state,
        initLoad: true,
        error: null,
        draft: initialDraftState,
      };
    case DELIVERY_ZONE_LOAD_ALL_FAILURE:
      return {
        ...state,
        initLoad: true,
        error: `Error loading zones! Reason: ${extractError(action)}`,
      };
    case DELIVERY_ZONE_CREATE_REQUEST:
      return {
        ...state,
        error: null,
      };
    case DELIVERY_ZONE_CREATE_FAILURE:
      return {
        ...state,
        error: `${extractError(action as ReturnType<typeof actions.createFailure>)}`,
      };
    case DELIVERY_ZONE_UPDATE_FAILURE:
      return {
        ...state,
        error: `${extractError(action as ReturnType<typeof actions.updateFailure>)}`,
      };
    case DELIVERY_ZONE_REMOVE_SUCCESS:
      return {
        ...state,
        error: null,
      };
    case DELIVERY_ZONE_REMOVE_FAILURE:
      return {
        ...state,
        error: `${extractError(action as ReturnType<typeof actions.removeFailure>)}`,
      };
    case DELIVERY_ZONE_STATE_RESET:
      return {
        ...state,
        ...initialState,
      };
    case DELIVERY_ZONE_ERROR_RESET:
      return {
        ...state,
        error: null,
      };
    case DELIVERY_ZONE_SELECTION_CHANGE:
      return {
        ...state,
        selectedId: (action as ReturnType<typeof actions.select>).payload,
      };
    case DELIVERY_ZONE_DRAFT_PATCH:
      return {
        ...state,
        draft: applyPatch(state.draft, action.payload as DeliveryZoneDraftPatchPayload),
      };
    case DELIVERY_ZONE_DRAFT_DISCARD:
    case DELIVERY_ZONE_DRAFT_CLEAR:
      return {
        ...state,
        draft: initialDraftState,
      };
    case DELIVERY_ZONE_DRAFT_SET_STATUS:
      return {
        ...state,
        draft: {
          ...state.draft,
          status: action.payload as DeliveryZonesDraftStatus,
        },
      };
    case DELIVERY_ZONE_DRAFT_SET_PROGRESS:
      return {
        ...state,
        draft: {
          ...state.draft,
          saveProgress: action.payload as DeliveryZonesDraftState['saveProgress'],
        },
      };
    case DELIVERY_ZONE_DRAFT_SET_ERROR:
      return {
        ...state,
        draft: {
          ...state.draft,
          lastError: action.payload as string | null,
          status: 'error',
        },
      };
    case DELIVERY_ZONE_DRAFT_SET_CONFLICT:
      return {
        ...state,
        draft: {
          ...state.draft,
          status: 'conflict',
          lastError: typeof action.payload === 'string' ? action.payload : state.draft.lastError,
        },
      };
    default:
      return state;
  }
}

function extractError(action) {
  let error = `Server error`;
  const errMsg = get(action, ['payload', 'response', 'body', 'Message']);
  if (errMsg) {
    error = errMsg;
  } else if (get(action, ['payload', 'response', 'statusCode']) === 404) {
    error = `Not found`;
  }
  return error;
}
